import React, { useEffect, useState } from "react";
import { Form, Input, Row, Col, Select } from "antd";
import { toUiPosition } from "../../utils/generalUtils";
import TermSelect from "../TermSelect";

import TextField from "./fields/TextField";
import TextAreaField from "./fields/TextAreaField";
import DateField from "./fields/DateField";
import DateRangeField from "./fields/DateRangeField";
import SelectField from "./fields/SelectField";
import MultipleSelectField from "./fields/MultipleSelectField";
import PositionDropdownField from "./fields/PositionDropdownField";
import RadioTextBoxField from "./fields/RadioTextBoxField";
import PeriodSelectorField from "./fields/PeriodSelectorField";
import AbsenceDateSelectorField from "./fields/AbsenceDateSelectorField";
import AccomplishmentsListField from "./fields/AccomplishmentsListField";
import ActivityHoursListField from "./fields/ActivityHoursListField";

const DynamicFormFields = ({
  fields,
  form,
  onRadioChange,
  previousData,
}) => {
  useEffect(() => {
    fields.forEach((field) => {
      if (field.type !== "position-dropdown") return;

      const currentId = form.getFieldValue(field.name);
      if (!currentId) return;

      const match = field.dropdownItems?.find(
        (p) => p.position_id === currentId
      );
      if (match) {
        const normalized = toUiPosition(match);
        form.setFieldsValue({
          [field.inputName]:
            normalized?.complete_position_name || normalized?.position_name,
        });
      }
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form]);

  const lastSubmission = previousData?.[previousData.length - 1];
  const previousAccomplishments = lastSubmission?.accomplishments;

  const [chairOptions, setChairOptions] = useState([]);
  const [deanOptions, setDeanOptions] = useState([]);
  const selectedPositionId = Form.useWatch("position_id", form);
  // Watches every field so a field's optional `shouldShow(values)` can react
  // to any other field's current value, not just position_id.
  const watchedValues = Form.useWatch([], form) || {};
  const visibleFields = fields.filter(
    (field) => !field.shouldShow || field.shouldShow(watchedValues)
  );

  useEffect(() => {
    const fetchChairsAndDeans = async () => {
      try {
        const token = localStorage.getItem("api_token");
        const [chairsRes, deansRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_API_URL}/position/chairs`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${process.env.REACT_APP_API_URL}/position/deans`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);
        const chairsData = (await chairsRes.json()).sort((a, b) => a.unit.localeCompare(b.unit));
        const deansData = (await deansRes.json()).sort((a, b) => a.unit.localeCompare(b.unit));
        setChairOptions(chairsData.map((p) => ({ label: `${p.position_name} - ${p.unit}`, value: p.id, unit: p.unit })));
        setDeanOptions(deansData.map((p) => ({ label: `${p.position_name} - ${p.unit}`, value: p.id, unit: p.unit })));
      } catch (err) {
        console.error("Failed to fetch chairs and deans", err);
      }
    };
    fetchChairsAndDeans();
  }, []);

  const positionField = fields.find((f) => f.name === "position_id");
  const selectedPosition = positionField?.dropdownItems?.find(
    (p) => p.position_id === selectedPositionId
  );

  useEffect(() => {
    if (!selectedPositionId || !deanOptions.length) {
      form.setFieldsValue({ dean: undefined, dean_label: "" });
      return;
    }
    if (!selectedPosition) {
      form.setFieldsValue({ dean: undefined, dean_label: "" });
      return;
    }
    const matchedDean = deanOptions.find((dean) => dean.unit === selectedPosition.unit);
    if (matchedDean) {
      form.setFieldsValue({ dean: matchedDean.value, dean_label: matchedDean.label });
    } else {
      form.setFieldsValue({ dean: undefined, dean_label: "" });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPositionId, deanOptions, form]);

  const renderField = (field) => {
    if (field.type === "radiotextbox") {
      return (
        <RadioTextBoxField
          key={field.name}
          field={field}
          form={form}
          onRadioChange={onRadioChange}
        />
      );
    }

    if (field.type === "daterange") {
      return <DateRangeField key={field.name} field={field} form={form} />;
    }

    if (field.type === "position-dropdown") {
      return (
        <PositionDropdownField key={field.name} field={field} form={form} />
      );
    }

    if (field.type === "select") {
      return <SelectField key={field.name} field={field} />;
    }

    if (field.type === "multiple-select") {
      return <MultipleSelectField key={field.name} field={field} />;
    }

    if (field.type === "period-selector") {
      return (
        <React.Fragment key={field.name}>
          <Form.Item
            hidden={field?.hidden}
            name={field.name}
            label={field.label}
            extra={field.extra}
            rules={field.rules}
          >
            <PeriodSelectorField
              onPeriodChange={(period) => {
                form.setFieldsValue({
                  start_date: period?.startDate ?? null,
                  end_date: period?.endDate ?? null,
                  absent_dates: "",
                });
              }}
            />
          </Form.Item>
          <Form.Item name="start_date" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="end_date" hidden>
            <Input />
          </Form.Item>
        </React.Fragment>
      );
    }

    if (field.type === "absence-chip-selector") {
      return (
        <Form.Item
          key={field.name}
          hidden={field?.hidden}
          name={field.name}
          label={field.label}
          extra={field.extra}
          rules={field.rules}
        >
          <AbsenceDateSelectorField form={form} />
        </Form.Item>
      );
    }

    if (field.type === "accomplishments-list") {
      return (
        <Form.Item
          key={field.name}
          hidden={field?.hidden}
          name={field.name}
          label={field.label}
          extra={field.extra}
          rules={field.rules}
        >
          <AccomplishmentsListField previousAccomplishments={previousAccomplishments} />
        </Form.Item>
      );
    }

    if (field.type === "activity-hours-list") {
      return <ActivityHoursListField key={field.name} form={form} />;
    }

    if (field.type === "textarea") {
      return <TextAreaField key={field.name} field={field} />;
    }

    if (field.type === "date") {
      return <DateField key={field.name} field={field} />;
    }

    if (field.type === "term-select") {
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} rules={field.rules}>
          <TermSelect allowOlderTerms={false} />
        </Form.Item>
      );
    }

    if (field.type === "appointment-status-select") {
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} rules={field.rules}>
          <Select
            showSearch
            style={{ width: "100%" }}
            options={[
              { label: "Permanent", value: "Permanent" },
              { label: "Temporary", value: "Temporary" },
              { label: "Substitute", value: "Substitute" },
            ]}
          />
        </Form.Item>
      );
    }

    if (field.type === "chair-select") {
      return (
        <Form.Item key={field.name} name={field.name} label={field.label} rules={field.rules}>
          <Select
            mode="multiple"
            allowClear
            style={{ width: "100%" }}
            options={chairOptions}
            optionFilterProp="label"
          />
        </Form.Item>
      );
    }

    if (field.type === "dean-select") {
      return (
        <React.Fragment key={field.name}>
          <Form.Item name="dean" hidden>
            <Input />
          </Form.Item>
          <Form.Item name="dean_label" label={field.label} rules={field.rules}>
            <Input disabled placeholder="No dean found for selected position" />
          </Form.Item>
        </React.Fragment>
      );
    }

    return <TextField key={field.name} field={field} />;
  };

  const hasTwoColumn = visibleFields.some(
    (f) => f.column === "left" || f.column === "right"
  );

  if (hasTwoColumn) {
    const leftFields = visibleFields.filter((f) => f.column !== "right");
    const rightFields = visibleFields.filter((f) => f.column === "right");
    return (
      <Row gutter={24} align="top">
        <Col span={12} style={{ borderRight: "1px solid #f0f0f0", paddingRight: 16 }}>
          {leftFields.map((field) => renderField(field))}
        </Col>
        <Col span={12}>
          {rightFields.map((field) => renderField(field))}
        </Col>
      </Row>
    );
  }

  return visibleFields.map((field) => renderField(field));
};

export default DynamicFormFields;
